import { A360Plan } from 'Quoting/models/Quoting';
import { CensusClassSelection } from '../Common/CensusClassSelection';
import { FormBlock, AvailablePlans, BlocksMap } from '../types';

// Participant Blocks
import { GPLUBenefitScheduleBlock } from './Participant/GPLUBenefitScheduleBlock';
import { GPLUEligibilityBlock } from './Participant/GPLUEligibilityBlock';
import { GPLUExclusionsAndLimitationsBlock } from './Participant/GPLUExclusionsAndLimitationsBlock';
import { GPLUFundingStructureBlock } from './Participant/GPLUFundingStructureBlock';
import { GPLUOptionsBlock } from './Participant/GPLUOptionsBlock';
import { GPLURatingBlock } from './Participant/GPLURatingBlock';
import { GPLUUnderWritingBlock } from './Participant/GPLUUnderWritingBlock';

// ChildTermLife Blocks
import { EligibilityBlock } from './ChildTermLife/GPLUEligibilityBlock';
import { ExclusionsAndLimitationsBlock } from './ChildTermLife/GPLUExclusionsAndLimitationsBlock';
import { RatingBlock } from './ChildTermLife/GPLURatingBlock';
import { UnderWritingBlock } from './ChildTermLife/GPLUUnderWritingBlock';
import { BenefitScheduleBlock } from './ChildTermLife/GPLUBenefitScheduleBlock';
import { CoverageDefinitionsBlock } from './ChildTermLife/GPLUCoverageDefinitionsBlock';

// Spouse Blocks
import { GPLUSpouseCoverageDefinitionsBlock } from './Spouse/GPLUSpouseCoverageDefinitionsBlock';
import { GPLUSpouseFundingStructureBlock } from './Spouse/GPLUSpouseFundingStructureBlock';
import { GPLUSpouseEligibilityBlock } from './Spouse/GPLUSpouseEligibilityBlock';
import { GPLUSpouseExclusionsAndLimitationsBlock } from './Spouse/GPLUSpouseExclusionsAndLimitationsBlock';
import { GPLUSpouseRatingBlock } from './Spouse/GPLUSpouseRatingBlock';
import { GPLUSpouseUnderWritingBlock } from './Spouse/GPLUSpouseUnderWritingBlock';
import { GPLUSpouseBenefitScheduleBlock } from './Spouse/GPLUSpouseBenefitScheduleBlock';
import { GPLUSpouseOptionsBlock } from './Spouse/GPLUSpouseOptionsBlock';
import { GPLUCoverageCode } from '../../PlanTemplates/GWL/gpluConstants';
import { GPLUAccelDeathCoverageDefinitionsBlock } from './AccelDeathBenefit/GPLUCoverageDefinitionsBlock';
import { GPLUAccelDeathEligibilityBlock } from './AccelDeathBenefit/GPLUEligibilityBlock';
import { GPLUAccelDeathExclusionsAndLimitationsBlock } from './AccelDeathBenefit/GPLUExclusionsAndLimitationsBlock';
import { GPLUAccelDeathRatingBlock } from './AccelDeathBenefit/GPLURatingBlock';
import { GPLUAccelDeathBenefitScheduleBlock } from './AccelDeathBenefit/GPLUBenefitScheduleBlock';
import { GPLUAccelDeathOptionsBlock } from './AccelDeathBenefit/GPLUOptionsBlock';

// Block Arrays
const WholeLife = (coverageCode: GPLUCoverageCode): FormBlock[] => {
  return [
    { FormBlock: () => CensusClassSelection(coverageCode), key: 'gplu-cesus-class-section' },
    { FormBlock: GPLUFundingStructureBlock, key: 'gplu-funding-structure-block' },
    { FormBlock: GPLUEligibilityBlock, key: 'gplu-eligibility-block' },
    { FormBlock: GPLUExclusionsAndLimitationsBlock, key: 'gplu-exclusions-and-limitations-block' },
    { FormBlock: GPLURatingBlock, key: 'gplu-rating-block' },
    { FormBlock: GPLUUnderWritingBlock, key: 'underwriting-block' },
    { FormBlock: GPLUBenefitScheduleBlock, key: 'benefit-schedule-block' },
    { FormBlock: GPLUOptionsBlock, key: 'options-block' },
  ]
};

const ChildTermLife = (coverageCode: GPLUCoverageCode): FormBlock[] => {
  return [
    { FormBlock: () => CensusClassSelection(coverageCode), key: 'gplu-cesus-class-section' },
    { FormBlock: CoverageDefinitionsBlock, key: 'gplu-coverage-definition-block' },
    { FormBlock: EligibilityBlock, key: 'gplu-eligibility-block' },
    { FormBlock: ExclusionsAndLimitationsBlock, key: 'gplu-exclusions-and-limitations-block' },
    { FormBlock: RatingBlock, key: 'gplu-rating-block' },
    { FormBlock: UnderWritingBlock, key: 'underwriting-block' },
    { FormBlock: BenefitScheduleBlock, key: 'benefit-schedule-block' },
  ]
};

const SpouseWholeLife = (coverageCode: GPLUCoverageCode): FormBlock[] => {
  return [
    { FormBlock: () => CensusClassSelection(coverageCode), key: 'gplu-cesus-class-section' },
    { FormBlock: GPLUSpouseCoverageDefinitionsBlock, key: 'gplu-coverage-definitions' },
    { FormBlock: GPLUSpouseFundingStructureBlock, key: 'gplu-funding-structure-block' },
    { FormBlock: GPLUSpouseEligibilityBlock, key: 'gplu-eligibility-block' },
    { FormBlock: GPLUSpouseExclusionsAndLimitationsBlock, key: 'gplu-exclusions-and-limitations-block' },
    { FormBlock: GPLUSpouseRatingBlock, key: 'gplu-rating-block' },
    { FormBlock: GPLUSpouseUnderWritingBlock, key: 'underwriting-block' },
    { FormBlock: GPLUSpouseBenefitScheduleBlock, key: 'benefit-schedule-block' },
    { FormBlock: GPLUSpouseOptionsBlock, key: 'options-block' },
  ];
};

export const AccelDeathBenefit = (coverageCode: GPLUCoverageCode): FormBlock[] => {
  return [
    { FormBlock: () => CensusClassSelection(coverageCode), key: 'gplu-census-class-section' },
    { FormBlock: GPLUAccelDeathCoverageDefinitionsBlock, key: 'gplu-coverage-definitions' },
    { FormBlock: GPLUAccelDeathEligibilityBlock, key: 'gplu-eligibility-block' },
    { FormBlock: GPLUAccelDeathExclusionsAndLimitationsBlock, key: 'gplu-exclusions-and-limitations-block' },
    { FormBlock: GPLUAccelDeathRatingBlock, key: 'gplu-rating-block' },
    { FormBlock: GPLUAccelDeathBenefitScheduleBlock, key: 'benefit-schedule-block' },
    { FormBlock: GPLUAccelDeathOptionsBlock, key: 'options-block' },
  ];
};

const permanentLifeBlocksMap: BlocksMap = new Map<A360Plan, FormBlock[]>();

export const getPermanentLifeBlocks = (plan: AvailablePlans, selectedLifeCoverage?: string): FormBlock[] => {
  if (!selectedLifeCoverage || plan !== A360Plan.GPLU) return [];

  switch (selectedLifeCoverage) {
    case GPLUCoverageCode.WHOLE_LIFE:
      permanentLifeBlocksMap.set(A360Plan.GPLU, WholeLife(selectedLifeCoverage));
      break;
    case GPLUCoverageCode.SPOUSE_WHOLE_LIFE:
      permanentLifeBlocksMap.set(A360Plan.GPLU, SpouseWholeLife(selectedLifeCoverage));
      break;
    case GPLUCoverageCode.CHILD_TERM_LIFE:
      permanentLifeBlocksMap.set(A360Plan.GPLU, ChildTermLife(selectedLifeCoverage));
      break;
    case GPLUCoverageCode.ACCEL_DEATH_BENEFIT_LIFE:
      permanentLifeBlocksMap.set(A360Plan.GPLU, AccelDeathBenefit(selectedLifeCoverage));
      break;
    default:
      permanentLifeBlocksMap.set(A360Plan.GPLU, WholeLife(GPLUCoverageCode.WHOLE_LIFE));
      break;
  }
  return permanentLifeBlocksMap.has(plan) ? permanentLifeBlocksMap.get(plan) : [];
};
